import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { LOGO, IMAGES } from "@/lib/constants";
import { generalWaLink } from "@/lib/whatsapp";
import { Sparkle } from "@/components/GoldOrnament";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero({ contact, images = {} }) {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bookLink = generalWaLink(contact?.phone_intl || "972555004933", lang);
  const heroImg = images?.hero || IMAGES.hero;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-ivory" data-testid="hero-section">
      {/* soft radial glow */}
      <div className="pointer-events-none absolute -top-40 end-[-10%] h-[520px] w-[520px] rounded-full bg-cream blur-3xl opacity-60" />
      <div className="pointer-events-none absolute bottom-[-15%] start-[-8%] h-[420px] w-[420px] rounded-full bg-rose blur-3xl opacity-50" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 md:pt-36 pb-16 grid lg:grid-cols-12 gap-10 items-center min-h-[100svh]">
        {/* Text column */}
        <div className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <img src={LOGO} alt="" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/40" />
            <span className="text-xs md:text-sm tracking-[0.22em] uppercase text-gold font-medium">{t.hero.overline}</span>
          </motion.div>

          <h1 className="font-serif text-ink leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            <span className="reveal-mask"><motion.span custom={0} variants={lineVariant} initial="hidden" animate="show" className="block">{t.hero.title1}</motion.span></span>
            <span className="reveal-mask"><motion.span custom={1} variants={lineVariant} initial="hidden" animate="show" className="block italic text-gold">{t.hero.title2}</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-md text-base md:text-lg text-taupe leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
          >
            <a
              href={bookLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-gold px-7 py-4 text-sm md:text-base font-medium text-white hover:brightness-105 hover:scale-[1.03] transition-[filter,transform]"
              data-testid="hero-book-button"
            >
              {t.hero.book}
            </a>
            <button
              onClick={() => scrollTo("services")}
              className="inline-flex items-center justify-center rounded-full border border-taupe/40 px-7 py-4 text-sm md:text-base font-medium text-ink hover:border-gold hover:text-gold transition-colors"
              data-testid="hero-discover-button"
            >
              {t.hero.discover}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-8 flex items-center gap-2 text-sm text-taupe"
          >
            <Sparkle size={14} />
            <span className="tracking-wide">{t.hero.mention}</span>
          </motion.div>
        </div>

        {/* Image column */}
        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[4/5] max-w-md mx-auto overflow-hidden rounded-t-[999px] rounded-b-[40px] ring-1 ring-gold/30"
          >
            <motion.img
              src={heroImg}
              alt="Soin du visage à L'Éclat du Regard"
              style={{ y: imgY }}
              className="absolute inset-0 h-[120%] w-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="absolute -bottom-4 start-2 md:start-0 bg-white/90 backdrop-blur rounded-2xl px-5 py-4 border border-sand shadow-sm"
          >
            <p className="font-serif text-2xl text-gold leading-none">Netanya</p>
            <p className="text-xs text-taupe mt-1">{t.hero.mention}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
